import { useEffect, useRef } from 'react';
import { apiUrl, CSRF_COOKIE_NAME } from '@/lib/api';

const MAX_SCROLL_DEPTH = 100;

export function useSpaAnalytics(path: string): void {
  const pathRef = useRef(path);
  const maxScrollDepthRef = useRef(0);

  useEffect(() => {
    flushScroll(pathRef.current, maxScrollDepthRef.current);
    pathRef.current = path;
    maxScrollDepthRef.current = 0;
    sendAnalyticsBeacon('/api/track/pageview', {
      path,
      referrer: document.referrer || null,
    });
  }, [path]);

  useEffect(() => {
    const updateScrollDepth = (): void => {
      maxScrollDepthRef.current = Math.max(maxScrollDepthRef.current, currentScrollDepth());
    };
    const flushCurrent = (): void => {
      flushScroll(pathRef.current, maxScrollDepthRef.current);
      maxScrollDepthRef.current = 0;
    };
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') flushCurrent();
    };

    updateScrollDepth();
    window.addEventListener('scroll', updateScrollDepth, { passive: true });
    window.addEventListener('beforeunload', flushCurrent);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('scroll', updateScrollDepth);
      window.removeEventListener('beforeunload', flushCurrent);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      flushCurrent();
    };
  }, []);
}

export function sendAnalyticsBeacon(endpoint: string, payload: unknown): boolean {
  if (typeof window === 'undefined') return false;
  const body = JSON.stringify(payload);
  const url = apiUrl(endpoint);
  const csrfToken = readCookie(CSRF_COOKIE_NAME);
  if (csrfToken !== null) {
    sendAnalyticsFetch(url, body, csrfToken);
    return false;
  }
  const sendBeacon = Reflect.get(navigator, 'sendBeacon') as
    | ((targetUrl: string, data?: BodyInit | null) => boolean)
    | undefined;
  if (typeof sendBeacon === 'function') {
    const blob = new Blob([body], { type: 'application/json' });
    if (sendBeacon.call(navigator, url, blob)) return true;
  }
  sendAnalyticsFetch(url, body, null);
  return false;
}

function sendAnalyticsFetch(url: string, body: string, csrfToken: string | null): void {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (csrfToken !== null) headers['X-CSRF-Token'] = csrfToken;
  void fetch(url, {
    method: 'POST',
    credentials: 'include',
    keepalive: true,
    headers,
    body,
  }).catch(() => undefined);
}

function readCookie(name: string): string | null {
  const prefix = `${encodeURIComponent(name)}=`;
  const found = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(prefix));
  if (!found) return null;
  return decodeURIComponent(found.slice(prefix.length));
}

function flushScroll(path: string, depth: number): void {
  if (depth <= 0) return;
  sendAnalyticsBeacon('/api/track/scroll', {
    path,
    scrollDepthPct: depth,
  });
}

function currentScrollDepth(): number {
  const doc = document.documentElement;
  const body = document.body;
  const scrollTop = window.scrollY || doc.scrollTop || body.scrollTop || 0;
  const scrollHeight = Math.max(doc.scrollHeight, body.scrollHeight);
  const viewport = window.innerHeight || doc.clientHeight || 1;
  const denominator = Math.max(1, scrollHeight - viewport);
  return Math.min(MAX_SCROLL_DEPTH, Math.max(0, Math.round((scrollTop / denominator) * 100)));
}
